import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, timer } from 'rxjs';
import { filter, map, switchMap, take, timeout } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { UserInterfaceService } from '../shared/services/user-interface.service';
import { UtilsService } from '../shared/services/utils.service';
import { DuplicateTableEntry } from './report/duplicate-table-entry';
import { Entity } from './report/entity';
import { MasterDuplicatesDetails } from './report/master-duplicates-details';

// TODO: rename to DuplikitService or split out into DuplikitSyncService, DuplikitMergeService, etc.
// TODO: Add typing for the responses of these calls.

@Injectable()
export class DuplicateService {
  results: DuplicateTableEntry[] = [];
  results$ = new BehaviorSubject<DuplicateTableEntry[]>(this.results);
  batchSizeLimit = 1000;

  constructor(private http: HttpClient,
              public userInterfaceService: UserInterfaceService,
              private utilsService: UtilsService) {
  }

  updateMaster(oldMaster: number, newMaster: number) {

    this.results.forEach(function (r) {
      if (r.masterID === oldMaster) {
        r.masterID = newMaster;
        r.duplicateIDs.delete(newMaster);
        r.duplicateIDs.add(oldMaster);
      }
    });
  }

  public refreshState(mergingEntity: string): void {
    const dupesForTableSub = this.getEntityDupesForTable(mergingEntity).subscribe(dupes => {
      this.results$.next(dupes.map(res => {
        res.button = 'MERGE';
        return res;
      }));
      this.userInterfaceService.duplikit.mergeButtonsDisabled = false;
    }, (error) => {
      console.error(`could not get ${ mergingEntity } dupes for table.`, error);
    }, () => dupesForTableSub.unsubscribe());
  }

  dedupeEntityBatch(entity: string, batchSize: number): Observable<boolean> {
    if (batchSize > this.batchSizeLimit) {
      batchSize = this.batchSizeLimit;
    } else if (batchSize <= 0) {
      batchSize = 1;
    }
    return this.http.get<{ success: boolean; actionId: string }>(`${ this.entityUrl(entity) }/dedupeBatched?batchSize=${ batchSize }`)
        .pipe(
          switchMap(({ success, actionId }) => {
            if (!success) {
              return of(false);
            }
            return timer(1_000, 10_000).pipe(
              switchMap(() => this.http.get<{ creating: boolean }>(`${ environment.duplikitUrl }/Dedupe/isCreatingTasks?actionId=${ actionId }`)),
              filter(res => !res.creating),
              take(1),
              map(() => true),
              timeout({first: 5 * 60 * 1000})
            );
          })
        );
  }

  dedupeEntity(entity: string, masterID: number): Observable<boolean> {
    return this.http.get<any>(`${ this.entityUrl(entity) }/dedupeByID?masterID=${ masterID }`)
        .pipe(map((res) => res.success));
  }

  downloadEntityCSV(entity: string): Observable<any> {
    return this.http.get(`${ this.entityUrl(entity) }/downloadCSV`);
  }

  getExportStatus(entity: string, jobId: string) {
    return this.http.get<{status: string, url?: string}>(`${ this.entityUrl(entity) }/getExportStatus?jobId=${jobId}`);
  }

  getEntityDupesForTable(entity: string): Observable<DuplicateTableEntry[]> {
    return this.http.get<DuplicateTableEntry[]>(`${ this.entityUrl(entity) }/getDupesForTable`)
        .pipe(map((res) => {
          return res.map((single) => {
            single.duplicateIDs = new Set(single.duplicateIDs);

            return single;
          });
        }));
  }

  getEntityDuplicateTotal(entity: string): Observable<number> {
    return this.http.get<number>(`${ this.entityUrl(entity) }/duplicateCount`);
  }

  mergeSelectedEntity(entity: string, masterID: number, dupeIDs: number[]): Observable<boolean> {
    return this.http.get<any>(`${ this.entityUrl(entity) }/dedupeByIDs?masterID=${ masterID }&entityIDs=${ dupeIDs }`)
        .pipe(map((res) => res.success));
  }

  updateMasterIDEntity(entity: string, oldID: number, newID: number): Observable<any> {
    this.updateMaster(oldID, newID);
    return this.http.get<any>(`${ this.entityUrl(entity) }/updateMasterID?oldID=${ oldID }&newID=${ newID }`);
  }

  markAsNotDuplicatesByID(entity: string, masterID: number, nonDupeIDs: number[]): Observable<any> {
    return this.http.get<any>(`${ this.entityUrl(entity) }/nonDupes?masterID=${ masterID }&nonDupeIDs=${ nonDupeIDs }`);
  }

  runFinderScript(entity: string): Observable<any> {
    return this.http.get<any>(`${ this.entityUrl(entity) }/runFinderScript`);
  }

  pollEntityDedupeState(entity: string): Observable<any> {
    return this.http.get<any>(`${ environment.duplikitUrl }/${ entity }/dedupeState`);
  }

  entityUrl(e: string) {
    return `${ environment.duplikitUrl }/${ this.utilsService.getProperEntityName(e) }`;
  }

  validateCustomQuery(entity: string, queryString: string): Observable<number> {
    return this.http.post<any>(`${environment.duplikitUrl}/settings/validateCustomQuery`,
      {entity: entity, queryString: queryString});
  }

  getMasterDupePairingsForDedupeTask(dedupeTaskId: string) {
    return this.http.get<any>(`${ environment.duplikitUrl }/Dedupe/getMasterDupePairingsForDedupeTask?dedupeTaskId=${ dedupeTaskId }`);
  }

  checkAuth(): Observable<any> {
    return this.http.get<any>(`${ environment.duplikitUrl }/auth/checkAuth`);
  }

  checkAdminUser() {
    return this.http.get<any>(`${ environment.duplikitUrl }/auth/checkAdminUser`);
  }

  clearResults(): void {
    this.results$.next(null);
  }

  getDuplicateDataForMaster(masterId: number, entity: Entity): Observable<MasterDuplicatesDetails> {
    return this.http.get<MasterDuplicatesDetails>
    (`${environment.duplikitUrl}/${this.utilsService.getProperEntityName(entity)}/getDetails?masterID=${masterId}`);
  }
}
